import { PLATE_LEAD_MS } from "@/lib/reveal-motion";
import type {
  NativeGlassFrame,
  NativeGlassMorph,
} from "./native-glass-geometry";
import { requestNativeGlassRefresh } from "./native-glass-settings";
import type { NativeGlassRefreshDetail } from "./native-glass-types";

export interface NativeGlassMeasuredRect extends NativeGlassFrame {
  right: number;
  bottom: number;
  left: number;
  top: number;
}

let motionRects = new WeakMap<HTMLElement, NativeGlassFrame>();
const morphs = new WeakMap<HTMLElement, NativeGlassMorph>();
let morphSequence = 0;
const MORPH_START_TIMEOUT_MS = 2000;
type PendingStart = {
  element: HTMLElement;
  morph: NativeGlassMorph;
  onStarted: (startedAt: number) => void;
  onTimeout: () => void;
  onCancelled?: () => void;
  timer: number;
};
const pendingStarts = new Map<string, PendingStart>();

function clearPendingStart(id: string): void {
  const pending = pendingStarts.get(id);
  if (pending) window.clearTimeout(pending.timer);
  pendingStarts.delete(id);
}

/** Receipts are scoped to this webview; stale or duplicate IDs are ignored. */
export function acceptNativeGlassMorphStarts(payload: unknown): void {
  if (!payload || typeof payload !== "object") return;
  const starts = (payload as { starts?: unknown }).starts;
  if (!Array.isArray(starts)) return;
  for (const start of starts) {
    if (
      !start ||
      typeof start.id !== "string" ||
      !Number.isFinite(start.startedAt)
    )
      continue;
    const pending = pendingStarts.get(start.id);
    if (!pending) continue;
    clearPendingStart(start.id);
    if (morphs.get(pending.element) !== pending.morph) continue;
    pending.morph.startedAt = start.startedAt;
    scheduleMorphEnd(pending.morph);
    pending.onStarted(start.startedAt);
  }
}

export function setNativeGlassMotionRect(
  element: HTMLElement,
  rect: NativeGlassFrame,
): void {
  motionRects.set(element, rect);
}

export function clearNativeGlassMotionRect(element: HTMLElement): void {
  motionRects.delete(element);
}

export function readNativeGlassMotionRect(
  element: HTMLElement,
): NativeGlassFrame | undefined {
  return motionRects.get(element);
}

export function resolveNativeGlassMeasuredRect(
  element: HTMLElement,
  domRect: DOMRect,
  scale: number,
): NativeGlassMeasuredRect {
  const source = readNativeGlassMotionRect(element) ?? domRect;
  return {
    x: source.x * scale,
    y: source.y * scale,
    width: source.width * scale,
    height: source.height * scale,
    right: (source.x + source.width) * scale,
    bottom: (source.y + source.height) * scale,
    left: source.x * scale,
    top: source.y * scale,
  };
}

export function resetNativeGlassMotionRects(): void {
  motionRects = new WeakMap();
}

const MORPH_REFRESH: NativeGlassRefreshDetail = {
  forceSend: true,
  trackTransition: false,
};
const requestMorphRefresh = () => requestNativeGlassRefresh(MORPH_REFRESH);
const scheduleMorphEnd = (morph: NativeGlassMorph) =>
  window.setTimeout(
    requestMorphRefresh,
    Math.max(0, morph.startedAt + morph.durationMs - Date.now()),
  );

/**
 * Hands the element's plate motion to native on the shared wall clock. The
 * collector sends the morph with the element's rect until it ends; a refresh
 * at the end sends the morph-less batch that settles the plate.
 */
export function startNativeGlassMorph(
  element: HTMLElement,
  motion: Omit<NativeGlassMorph, "id" | "startedAt">,
  start?: {
    onStarted: (startedAt: number) => void;
    onTimeout: () => void;
    onCancelled?: () => void;
  },
): NativeGlassMorph {
  const previous = morphs.get(element);
  if (previous) clearPendingStart(previous.id);
  const morph: NativeGlassMorph = {
    ...motion,
    id: `morph-${Date.now().toString(36)}-${++morphSequence}`,
    startedAt: Date.now() + PLATE_LEAD_MS,
  };
  morphs.set(element, morph);
  if (motion.startOnApply && start) {
    const timer = window.setTimeout(() => {
      const pending = pendingStarts.get(morph.id);
      if (!pending) return;
      clearPendingStart(morph.id);
      morphs.delete(element);
      start.onTimeout();
      requestMorphRefresh();
    }, MORPH_START_TIMEOUT_MS);
    pendingStarts.set(morph.id, { element, morph, ...start, timer });
  } else scheduleMorphEnd(morph);
  requestMorphRefresh();
  return morph;
}

/** Stops an in-flight plate morph and sends its settled glass state. */
export function cancelNativeGlassMorph(element: HTMLElement): void {
  const morph = morphs.get(element);
  const onCancelled = morph
    ? pendingStarts.get(morph.id)?.onCancelled
    : undefined;
  if (morph) clearPendingStart(morph.id);
  if (!morphs.delete(element)) return;
  onCancelled?.();
  requestMorphRefresh();
}

/** The element's morph until it ends. */
export function readNativeGlassMorph(
  element: HTMLElement,
): NativeGlassMorph | undefined {
  const morph = morphs.get(element);
  if (morph && pendingStarts.has(morph.id)) return morph;
  if (!morph || Date.now() < morph.startedAt + morph.durationMs) return morph;
  morphs.delete(element);
  return undefined;
}

/** Maps a morph's epoch `startedAt` onto the document timeline (WAAPI). */
export function webAnimationStartTime(startedAt: number): number {
  return performance.now() + (startedAt - Date.now());
}
